import { useState, useMemo } from "react";
import { Users, Clock, AlertTriangle, CheckCircle } from "lucide-react";
import { format, addDays } from "date-fns";
import { useJobs } from "../../jobs/hooks/useJobs";
import { useEmployees } from "../../employees/hooks/useEmployees";

interface CapacityData {
  date: string;
  totalEmployees: number;
  availableEmployees: number;
  scheduledJobs: number;
  unassignedJobs: number;
  remainingCapacity: number;
  isOverCapacity: boolean;
}

export default function CapacityOverview() {
  const { jobs } = useJobs();
  const { employees } = useEmployees();

  const [selectedTimeSlot, setSelectedTimeSlot] = useState("09:00");

  const timeSlots = [
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
  ];

  // Generate next 7 days of capacity data using useMemo
  const capacityData = useMemo(() => {
    const data: CapacityData[] = [];
    const today = new Date();

    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      const dateString = format(date, "yyyy-MM-dd");

      const activeEmployees = employees.filter(
        (emp) => emp.status === "active"
      );

      // Count scheduled jobs for this date and time
      const scheduledJobs = jobs.filter(
        (job) =>
          job.date_time === dateString &&
          job.status !== "cancelled" &&
          job.business
      ).length;

      // Count unassigned jobs (those without business)
      const unassignedJobs = jobs.filter(
        (job) =>
          job.date_time === dateString &&
          job.status !== "cancelled" &&
          !job.business
      ).length;

      // Calculate available employees (total - scheduled)
      const availableEmployees = Math.max(
        0,
        activeEmployees.length - scheduledJobs
      );
      const remainingCapacity = Math.max(
        0,
        availableEmployees - unassignedJobs
      );
      const isOverCapacity = unassignedJobs > availableEmployees;

      data.push({
        date: dateString,
        totalEmployees: activeEmployees.length,
        availableEmployees,
        scheduledJobs,
        unassignedJobs,
        remainingCapacity,
        isOverCapacity,
      });
    }

    return data;
  }, [employees, jobs]);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "EEE, MMM d");
  };

  const getCapacityStatus = (capacity: CapacityData) => {
    if (capacity.isOverCapacity) {
      return { color: "text-red-600", bg: "bg-red-50", icon: AlertTriangle };
    } else if (capacity.remainingCapacity === 0) {
      return { color: "text-yellow-600", bg: "bg-yellow-50", icon: Clock };
    } else {
      return { color: "text-green-600", bg: "bg-green-50", icon: CheckCircle };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Users className="h-5 w-5 mr-2 text-indigo-600" />
          Employee Capacity Overview
        </h2>

        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">
            Time Slot:
          </label>
          <select
            value={selectedTimeSlot}
            onChange={(e) => setSelectedTimeSlot(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {timeSlots.map((time) => (
              <option key={time} value={time}>
                {format(new Date(`2000-01-01T${time}`), "h:mm a")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Active Employees</p>
              <p className="text-2xl font-bold text-gray-900">
                {employees.filter((emp) => emp.status === "active").length}
              </p>
            </div>
            <Users className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Peak Utilization</p>
              <p className="text-2xl font-bold text-gray-900">
                {capacityData.length > 0
                  ? Math.max(
                      ...capacityData.map((d) =>
                        Math.round((d.scheduledJobs / d.totalEmployees) * 100)
                      )
                    )
                  : 0}
                %
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Over-Capacity Days</p>
              <p className="text-2xl font-bold text-gray-900">
                {capacityData.filter((d) => d.isOverCapacity).length}
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Capacity Details */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            7-Day Capacity Forecast for{" "}
            {format(new Date(`2000-01-01T${selectedTimeSlot}`), "h:mm a")}
          </h3>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {capacityData.map((capacity) => {
              const status = getCapacityStatus(capacity);
              const StatusIcon = status.icon;

              return (
                <div
                  key={capacity.date}
                  className={`p-4 rounded-lg border ${status.bg} border-gray-200`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <StatusIcon className={`h-5 w-5 ${status.color}`} />
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDate(capacity.date)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {capacity.scheduledJobs} scheduled,{" "}
                          {capacity.unassignedJobs} unassigned
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center space-x-4 text-sm">
                        <div>
                          <span className="text-gray-600">Available:</span>
                          <span className="font-medium ml-1">
                            {capacity.availableEmployees}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Remaining:</span>
                          <span
                            className={`font-medium ml-1 ${
                              capacity.remainingCapacity < 0
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {capacity.remainingCapacity}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {capacity.isOverCapacity && (
                    <div className="mt-2 text-sm text-red-600">
                      ⚠️ Over-capacity: {Math.abs(capacity.remainingCapacity)}{" "}
                      more employees needed
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
